import React from "react";
import { Link } from "react-router-dom";
import axios from "axios";

import ModalManager from "../../components/common/modal/ModalManager";
import TextInput from "../../components/common/inputs/textInput";
import NotificationManager from "../../utils/notificationManager";

import "./deviceGroups.css";

const aggregateStats = (groups = []) => {
  return groups.reduce(
    (accumulator, group) => {
      const totalOutlets = Array.isArray(group.items)
        ? group.items.length
        : Number(group.itemsCount) || 0;

      return {
        groupCount: accumulator.groupCount + 1,
        totalOutlets: accumulator.totalOutlets + totalOutlets,
      };
    },
    {
      groupCount: 0,
      totalOutlets: 0,
    }
  );
};

const formatNumber = (value) => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value.toLocaleString();
  }

  return value;
};

export default class OutletGroups extends React.Component {
  state = {
    searchBar: "",
    currentFilter: "",
  };

  componentDidMount() {
    this.ensureGroupsLoaded();
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.resources.groupsLoaded !== this.props.resources.groupsLoaded &&
      !this.props.resources.groupsLoaded &&
      !this.props.resources.groupsLoading
    ) {
      this.ensureGroupsLoaded();
    }
  }

  ensureGroupsLoaded = () => {
    const { resources = {}, fetchResource } = this.props;
    if (!resources.groupsLoaded && !resources.groupsLoading) {
      fetchResource("groups");
    }
  };

  handleSearchChange = (event) => {
    this.setState({ searchBar: event.target.value });
  };

  handleSearchKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      this.applyFilters();
    }
  };

  applyFilters = () => {
    this.setState({ currentFilter: this.state.searchBar });
  };

  clearFilters = () => {
    this.setState({
      searchBar: "",
      currentFilter: "",
    });
  };

  openCreateModal = () => {
    ModalManager.confirm(
      "",
      "Create Outlet Group",
      "",
      (submitted, close) => {
        if (submitted) {
          close();
        }
      },
      ({ close }) => (
        <CreateOutletGroupForm
          onSubmit={this.handleCreateGroup}
          close={close}
        />
      ),
      true,
      true
    );
  };

  handleCreateGroup = async ({ name, description }, _ignored, close) => {
    if (!name) {
      NotificationManager.alert("Outlet group name is required", "Error");
      // error already surfaced
      return;
    }

    try {
      const response = await axios.post(
        "/v1/groups",
        {
          name,
          description,
          groupType: "outlets",
          items: [],
        },
        {
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
        }
      );

      NotificationManager.success("Outlet group created");
      await this.props.fetchResource("groups");

      const newGroupId = response?.data?.data?.id;
      close && close();
      if (newGroupId) {
        this.props.history.push(`/devices/outlet-groups/${newGroupId}`);
      }
    } catch (error) {
      console.error(error);
      NotificationManager.alert("Failed to create outlet group", "Error");
      // done && done();
    }
  };

  render() {
    const { resources = {}, user } = this.props;
    const { groupsLoading } = resources;
    const isAdmin =
      user.has_admin_privileges || user.is_enterprise_admin;

    const groups = (resources.groups || []).filter(
      (group) => group.groupType === "outlets"
    );

    if (groupsLoading && groups.length === 0) {
      return (
        <div className="device-groups-page">
          <div className="device-groups-header">
            <div>
              <h2 className="device-groups-header__title">Manage outlet groups</h2>
            </div>
          </div>
          <div className="device-groups-summary">
            {[1, 2, 3].map((card) => (
              <div
                key={card}
                className="device-groups-summary-card device-groups-skeleton-card"
              >
                <div className="skeleton-blank" style={{ width: "40%" }} />
                <div
                  className="skeleton-blank"
                  style={{ width: "70%", height: "24px" }}
                />
                <div className="skeleton-blank" style={{ width: "55%" }} />
                <div className="device-groups-summary-card__accent" />
              </div>
            ))}
          </div>
        </div>
      );
    }

    if (groups.length === 0) {
      return (
        <div className="device-groups-page">
          <div className="device-groups-header">
            <div>
              <h2 className="device-groups-header__title">Manage outlet groups</h2>
              <div className="device-groups-header__description">
                Organize outlets across all PDUs, then reuse those groups for assignments,
                automation, and reporting.
              </div>
            </div>
            {isAdmin && (
              <div className="device-groups-header__actions">
                <button className="button" onClick={this.openCreateModal}>
                  Create outlet group
                </button>
              </div>
            )}
          </div>
          <div className="device-groups-empty">
            <i className="material-icons device-groups-empty__icon">outlet</i>
            <div className="device-groups-empty__title">No outlet groups yet</div>
            <div className="device-groups-empty__description">
              Organize commonly used outlets into named groups. Create your first group
              to start tracking equipment across PDUs.
            </div>
            {isAdmin && (
              <div className="device-groups-empty__actions">
                <button className="button" onClick={this.openCreateModal}>
                  Create outlet group
                </button>
              </div>
            )}
          </div>
        </div>
      );
    }

    const { currentFilter, searchBar } = this.state;
    const filteredGroups = groups.filter((group) => {
      const haystack = `${group.name || ""} ${group.description || ""}`.toLowerCase();
      return haystack.includes(currentFilter.toLowerCase());
    });

    const overallStats = aggregateStats(groups);
    const filteredStats = aggregateStats(filteredGroups);

    return (
      <div className="device-groups-page">
        <div className="device-groups-header">
          <div>
            <h2 className="device-groups-header__title">Manage outlet groups</h2>
            <div className="device-groups-header__description">
              Group outlets across PDUs to track equipment, delegate access,
              and take action faster.
            </div>
          </div>
          {isAdmin && (
            <div className="device-groups-header__actions">
              <button className="button" onClick={this.openCreateModal}>
                Create outlet group
              </button>
            </div>
          )}
        </div>

        <div className="device-groups-summary">
          <SummaryCard
            label="Outlet groups"
            value={formatNumber(filteredStats.groupCount)}
            meta={
              overallStats.groupCount > 0
                ? `of ${formatNumber(overallStats.groupCount)} total`
                : "Filtered selection"
            }
            accentColor="#2563eb"
          />
          <SummaryCard
            label="Total outlets"
            value={formatNumber(filteredStats.totalOutlets)}
            meta={
              overallStats.totalOutlets > 0
                ? `of ${formatNumber(overallStats.totalOutlets)} total`
                : "Across filtered groups"
            }
            accentColor="#10b981"
          />
        </div>

        <div className="device-groups-toolbar">
          <div className="device-groups-search">
            <input
              type="text"
              className="device-groups-search__input"
              placeholder="Search by group name or description"
              value={searchBar}
              onChange={this.handleSearchChange}
              onKeyDown={this.handleSearchKeyDown}
            />
            <button
              type="button"
              className="search-icon-button device-groups-search__button"
              onClick={this.applyFilters}
              aria-label="Search outlet groups"
            >
              <i className="material-icons">search</i>
            </button>
          </div>
          <div className="device-groups-toolbar__meta">
            {filteredGroups.length === 0
              ? "No outlet groups match your filters"
              : `Showing ${formatNumber(filteredGroups.length)} of ${formatNumber(
                  groups.length
                )} outlet groups`}
          </div>
        </div>

        {filteredGroups.length === 0 ? (
          <div className="device-groups-empty">
            <i className="material-icons device-groups-empty__icon">filter_alt</i>
            <div className="device-groups-empty__title">
              No outlet groups match your search
            </div>
            <div className="device-groups-empty__description">
              Try adjusting your keywords or clear the filter to see every outlet group.
            </div>
            <div className="device-groups-empty__actions">
              <button
                type="button"
                className="device-groups-table__manage"
                onClick={this.clearFilters}
              >
                Clear search
              </button>
            </div>
          </div>
        ) : (
          <div className="device-groups-table-wrapper">
            <div className="device-groups-table device-groups-table--outlets">
                <div className="device-groups-table__head">
                  <div>Outlet group</div>
                  <div>Outlets</div>
                  <div>Actions</div>
                </div>
                {filteredGroups.map((group) => {
                  const totalOutlets = Array.isArray(group.items)
                    ? group.items.length
                    : 0;
                  const description = group.description?.trim()
                    ? group.description
                    : "No description provided";

                return (
                  <div className="device-groups-table__row" key={group.id}>
                    <div className="device-groups-table__name">
                      <Link to={`/devices/outlet-groups/${group.id}`}>
                        {group.name}
                      </Link>
                      <div className="device-groups-table__description">
                        {description}
                      </div>
                    </div>
                    <div className="device-groups-table__metric">
                      <strong>{formatNumber(totalOutlets)}</strong>
                      <span>Total outlets</span>
                    </div>
                    <div className="device-groups-table__actions">
                      <Link
                        to={`/devices/outlet-groups/${group.id}`}
                        className="device-groups-table__manage"
                      >
                        View group
                      </Link>
                      {isAdmin && (
                        <Link
                          to={`/devices/outlet-groups/${group.id}/settings`}
                          className="device-groups-table__manage"
                        >
                          Manage
                        </Link>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  }
}

const SummaryCard = ({ label, value, meta, accentColor }) => (
  <div className="device-groups-summary-card">
    <div className="device-groups-summary-card__label">{label}</div>
    <div className="device-groups-summary-card__value">{value}</div>
    <div className="device-groups-summary-card__meta">{meta}</div>
    <div
      className="device-groups-summary-card__accent"
      style={{ backgroundColor: accentColor }}
    />
  </div>
);

class CreateOutletGroupForm extends React.Component {
  state = {
    name: "",
    description: "",
    loading: false,
  };

  handleSubmit = async () => {
    if (!this.state.name.trim()) {
      NotificationManager.alert("Group name is required", "Error");
      return;
    }
    this.setState({ loading: true });
    try {
      await this.props.onSubmit(
        {
          name: this.state.name.trim(),
          description: this.state.description.trim(),
        },
        null,
        this.props.close
      );
    } finally {
      this.setState({ loading: false });
    }
  };

  render() {
    return (
      <div style={{ minWidth: "320px" }}>
        <TextInput
          label="Group name"
          value={this.state.name}
          callback={(value) => this.setState({ name: value })}
        />
        <TextInput
          label="Description"
          value={this.state.description}
          callback={(value) => this.setState({ description: value })}
          placeholder="Optional"
        />
        <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
          <button
            className="button secondary"
            onClick={() => this.props.close()}
            disabled={this.state.loading}
          >
            Cancel
          </button>
          <button
            className="button"
            onClick={this.handleSubmit}
            disabled={this.state.loading}
          >
            {this.state.loading ? "Saving..." : "Create"}
          </button>
        </div>
      </div>
    );
  }
}
